import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { ROUTES } from '../../routes';
import { applyTheme, readStoredDark } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { BrandMarkJ } from '@/components/brand/BrandMark';

function normalizePath(p: string) {
  const t = p.trim();
  return (t.endsWith('/') && t.length > 1 ? t.slice(0, -1) : t) || '/';
}

interface Props {
  /** Extra controls on the right (e.g. signed-in user menu on invite completion). */
  end?: React.ReactNode;
}

/** Minimal chrome for unauthenticated pages (login, invite, etc.). */
export const PublicTopBar: React.FC<Props> = ({ end }) => {
  const { pathname, search } = useLocation();
  const onLogin = pathname === ROUTES.LOGIN;
  const onOwnerInvite = normalizePath(pathname) === normalizePath(ROUTES.OWNER_INVITE);
  const tenantFromInvite = onOwnerInvite ? new URLSearchParams(search).get('tenantId')?.trim() : '';
  const signInTo =
    !onLogin && tenantFromInvite
      ? `${ROUTES.LOGIN}?tenantId=${encodeURIComponent(tenantFromInvite)}`
      : ROUTES.LOGIN;

  const [dark, setDark] = useState(readStoredDark);
  useEffect(() => {
    applyTheme(dark);
  }, [dark]);
  const toggle = useCallback(() => setDark(d => !d), []);

  return (
    <header className="flex shrink-0 items-center justify-between border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-[var(--color-surface)] px-4 py-3">
      <Link
        to="/"
        className="flex items-center gap-2 text-sm font-bold tracking-tight text-neutral-900 dark:text-neutral-100 hover:text-[color:var(--color-primary)]"
      >
        <BrandMarkJ size="sm" />
        JioBuzzAI
      </Link>
      <nav className="flex items-center gap-2 text-sm" aria-label="Account">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-neutral-600 dark:text-neutral-300"
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggle}
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        {!onLogin && !end && (
          <Link to={signInTo} className="font-medium text-brand-700 dark:text-rose-300 hover:underline px-2">
            Sign in
          </Link>
        )}
        {end}
      </nav>
    </header>
  );
};
